import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUser } from "../context/AuthContext";
import compositionOptions from "../utils/compositionModels";
import "./DebatModePanelComposition.css";

const TIP_GAP = 8;
const TIP_MARGIN = 12;

/* The tooltip renders in a portal on <body>, not beside its trigger.
   Two ancestors made in-place positioning impossible:
     - .debate-page-content is overflow-y:auto, which CLIPS absolutely
       positioned descendants no matter their z-index (measured: 16px cut at
       1920, 131px at 1280, and unhittable there).
     - .debate-main-card carries a transform, and a transformed ancestor
       becomes the containing block for position:fixed too — so even fixed
       stayed trapped and clipped.
   A portal escapes both. Because the tooltip is then no longer a DOM
   descendant of the trigger, CSS :hover cannot drive it and visibility is
   React state instead. */
function computeTipPosition(trigger, tip) {
  const t = trigger.getBoundingClientRect();
  const w = tip.offsetWidth;
  const h = tip.offsetHeight;
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  let left = t.left + t.width / 2 - w / 2;
  left = Math.max(TIP_MARGIN, Math.min(left, vw - w - TIP_MARGIN));

  const below = t.bottom + TIP_GAP;
  // Flip above the trigger only when there is genuinely more room up there.
  const flip = below + h > vh - TIP_MARGIN && t.top - TIP_GAP - h > TIP_MARGIN;
  const top = Math.max(
    TIP_MARGIN,
    Math.min(flip ? t.top - TIP_GAP - h : below, vh - h - TIP_MARGIN),
  );

  return {
    left: Math.round(left),
    top: Math.round(top),
    placement: flip ? "top" : "bottom",
    // Keep the arrow on the icon even after the box is clamped to the viewport.
    arrowX: Math.round(t.left + t.width / 2 - left),
  };
}

export default function DebatModePanelComposition({ composition, setComposition, disabled }) {
  const user  = useUser();
  const isTrial = user?.subscriptionStatus === "trialing" || !user;
  const tipWrapperRef = useRef(null);
  const tipRef = useRef(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipPos, setTipPos] = useState(null);

  // The trial-lock tooltip needs the same portal treatment as the info one.
  // Hovering the option applies `transform: translateY(-2px)`, and a
  // transformed ancestor creates a stacking context — so the tooltip's
  // z-index:99999 only competed INSIDE that context and the rounds row above
  // painted straight over it (measured: covered by .debate-controls-top-inner).
  const trialAnchorRef = useRef(null);
  const trialTipRef = useRef(null);
  const [trialOpen, setTrialOpen] = useState(false);
  const [trialPos, setTrialPos] = useState(null);

  const openTip = useCallback(() => setTipOpen(true), []);
  const closeTip = useCallback(() => setTipOpen(false), []);
  const openTrial = useCallback(() => setTrialOpen(true), []);
  const closeTrial = useCallback(() => setTrialOpen(false), []);

  // Measure after the portal paints, before the browser shows it.
  useLayoutEffect(() => {
    if (!tipOpen || !tipWrapperRef.current || !tipRef.current) return;
    setTipPos(computeTipPosition(tipWrapperRef.current, tipRef.current));
  }, [tipOpen]);

  useLayoutEffect(() => {
    if (!trialOpen || !trialAnchorRef.current || !trialTipRef.current) return;
    setTrialPos(computeTipPosition(trialAnchorRef.current, trialTipRef.current));
  }, [trialOpen]);

  // A fixed tooltip does not travel with the page, so close it on scroll or
  // resize rather than leaving it stranded next to nothing.
  useEffect(() => {
    if (!tipOpen && !trialOpen) return undefined;
    const onDismiss = () => { setTipOpen(false); setTrialOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { setTipOpen(false); setTrialOpen(false); } };
    window.addEventListener("scroll", onDismiss, true);
    window.addEventListener("resize", onDismiss);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onDismiss, true);
      window.removeEventListener("resize", onDismiss);
      window.removeEventListener("keydown", onKey);
    };
  }, [tipOpen, trialOpen]);

  const handleChange = (key) => {
    if (disabled) return;
    const next = compositionOptions.find(
      (option) => option.value === key,
    );
    if (next) setComposition(next);
  };

  const selectedValue = composition?.value ?? composition?.key ?? "standard-panel";

  return (
    <div className="debat-mode-panel-composition">
      <div className="debat-mode-panel-title-container">
        <h1 className="debat-mode-panel-composition-title">
          Debate Panel Composition
        </h1>
        <div
          className="debat-mode-panel-tooltip-wrapper"
          ref={tipWrapperRef}
          onMouseEnter={openTip}
          onMouseLeave={closeTip}
          onFocus={openTip}
          onBlur={closeTip}
          tabIndex={0}
          aria-describedby={tipOpen ? "debate-panel-tip" : undefined}
        >
          <img
            src="/question-mark-circle-svgrepo-com.svg"
            alt="Info"
            className="debat-mode-panel-info-icon"
          />
        </div>
        {tipOpen &&
          createPortal(
            <div
              id="debate-panel-tip"
              role="tooltip"
              ref={tipRef}
              className="debat-mode-panel-tooltip debat-mode-panel-tooltip--floating"
              data-placement={tipPos?.placement || "bottom"}
              style={{
                left: tipPos ? `${tipPos.left}px` : 0,
                top: tipPos ? `${tipPos.top}px` : 0,
                // Hidden for the first paint only, while it is measured off its
                // final position — otherwise it flashes in the corner.
                visibility: tipPos ? "visible" : "hidden",
                "--dw-tip-arrow-x": `${tipPos?.arrowX ?? 0}px`,
              }}
            >
              <div className="debat-mode-panel-tooltip-section">
                <strong>Standard Panel</strong>
                <ul>
                  <li>Balance legal reasoning and structured debate.</li>
                  <li>Best for comprehensive analysis.</li>
                </ul>
              </div>
              <div className="debat-mode-panel-tooltip-divider"></div>
              <div className="debat-mode-panel-tooltip-section">
                <strong>Adversarial Panel</strong>
                <ul>
                  <li>Aggressive stress testing and procedural challenges.</li>
                  <li>
                    Best for finding weaknesses before opposing counsel does.
                  </li>
                </ul>
              </div>
            </div>,
            document.body,
          )}
      </div>

      <div
        className="debat-mode-panel-composition-options"
        style={disabled ? { pointerEvents: "none" } : undefined}
      >
        {compositionOptions.map((option) => {
          const isSelected = selectedValue === option.value || selectedValue === option.key;
          const isUnselectedWhenLocked = disabled && !isSelected;
          const isTrialLocked = isTrial && option.value === "adversarial-panel";
          return (
          <div
            key={option.value}
            className={[
              "debat-mode-panel-composition-option",
              isUnselectedWhenLocked ? "debat-mode-panel-composition-option-locked-unselected" : "",
              isTrialLocked ? "debat-mode-panel-composition-option-trial-locked" : "",
            ].join(" ").trim()}
          >
            <input
              type="radio"
              name="debate-mode"
              id={option.value}
              hidden
              checked={isSelected}
              disabled={disabled || isTrialLocked}
              onChange={() => !isTrialLocked && handleChange(option.key)}
              readOnly={disabled || isTrialLocked}
            />
            <label
              htmlFor={option.value}
              className={`debat-mode-panel-composition-option-label${isTrialLocked ? " debat-mode-panel-composition-option-label-trial" : ""}`}
              ref={isTrialLocked ? trialAnchorRef : undefined}
              onMouseEnter={isTrialLocked ? openTrial : undefined}
              onMouseLeave={isTrialLocked ? closeTrial : undefined}
              onFocus={isTrialLocked ? openTrial : undefined}
              onBlur={isTrialLocked ? closeTrial : undefined}
            >
              <div className="debat-mode-panel-composition-option-icon">
                <div className="debat-mode-panel-composition-option-icon-inner"></div>
              </div>
              <div className="debat-mode-panel-composition-option-body">
                <div className="debat-mode-panel-composition-option-heading">
                  <strong>{option.label}</strong>
                </div>
                <div className="debat-mode-panel-composition-option-models-row">
                  {option.models.split(",").map((model) => {
                    const name = model.trim();
                    return (
                      <span
                        key={name}
                        className="debat-mode-panel-composition-option-model"
                      >
                        {name}
                      </span>
                    );
                  })}
                </div>
              </div>
              {isTrialLocked && (
                <>
                  <span
                    className="debat-mode-panel-trial-lock-icon"
                    aria-label="Paid plan required"
                  >
                    🔒
                  </span>
                  {trialOpen &&
                    createPortal(
                      <div
                        role="tooltip"
                        ref={trialTipRef}
                        className="debat-mode-panel-trial-hover-tooltip debat-mode-panel-tooltip--floating"
                        data-placement={trialPos?.placement || "bottom"}
                        style={{
                          left: trialPos ? `${trialPos.left}px` : 0,
                          top: trialPos ? `${trialPos.top}px` : 0,
                          visibility: trialPos ? "visible" : "hidden",
                          "--dw-tip-arrow-x": `${trialPos?.arrowX ?? 0}px`,
                        }}
                      >
                        Unlock deeper debates (8+ rounds), Adversarial Panel, and
                        revision tracking with a <a href="/pricing">paid plan</a>.
                      </div>,
                      document.body,
                    )}
                </>
              )}
            </label>
          </div>
          );
        })}
      </div>
    </div>
  );
}
